import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import axios from 'axios';
import { API_URL } from '../../config';

/**
 * DemoUsersPanel — install + re-sync curated demo users into the env.
 *
 * Each row is a fixture pack discovered under
 * onward_common/demo_users/{id}/. Sync copies all files into storage
 * at {user_id}/{relative_path} — idempotent, so re-running re-applies
 * the curated state.
 *
 * After sync, the user appears in the Users tab and is impersonable
 * via the standard View As flow.
 */
const StatusBadge = ({ installed }) =>
  installed ? (
    <Chip
      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
      label="installed"
      size="small"
      color="success"
      variant="outlined"
    />
  ) : (
    <Chip
      icon={<PersonOffIcon sx={{ fontSize: 14 }} />}
      label="not installed"
      size="small"
      color="default"
      variant="outlined"
    />
  );

const DemoUsersPanel = () => {
  const [demoUsers, setDemoUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState(null);

  const fetchDemoUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/demo-users`);
      setDemoUsers(data.demo_users || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDemoUsers();
  }, [fetchDemoUsers]);

  const sync = async (id) => {
    setBusyId(id);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/demo-users/sync`, { id });
      const skipMsg = (data.skipped || []).length
        ? ` (skipped: ${(data.skipped || []).map((s) => s.file).join(', ')})`
        : '';
      setFlash({
        severity: 'success',
        text: `Synced ${id} → ${data.user_id} · ${data.written.length} files written${skipMsg}`,
      });
      await fetchDemoUsers();
    } catch (e) {
      setFlash({
        severity: 'error',
        text: `Sync failed: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Curated demo users — install from in-package fixtures into this env's storage. Idempotent: re-syncing reverts any drift to the fixture state.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          After sync, find the user in the <strong>Users</strong> tab → View as. The pack ID maps to a stable user_id so impersonation is reproducible across envs.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {flash && (
        <Alert severity={flash.severity} sx={{ mb: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      {demoUsers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No demo user packs found. Add one under <code>onward_common/demo_users/</code>.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Demo user</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Files</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {demoUsers.map((u) => {
              const status = u.status || {};
              return (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {u.label}
                    </Typography>
                    {u.tagline && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontStyle: 'italic' }}
                      >
                        {u.tagline}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Persona: {u.persona || '—'} · user_id: <code>{u.user_id}</code>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <StatusBadge installed={!!status.installed} />
                    {status.installed && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {status.name} ({status.email})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          status: {status.status}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={0.25}>
                      {(u.files || []).map((f) => (
                        <Typography
                          key={f}
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                        >
                          {f}
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      disabled={busyId === u.id}
                      onClick={() => sync(u.id)}
                    >
                      {busyId === u.id ? 'Syncing…' : status.installed ? 'Re-sync' : 'Install'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default DemoUsersPanel;
